import { useState } from "react";
import { Radio, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../../api/client";
import { twilioApi, type TwilioView } from "../../../../api/twilio";
import { Button, TextInput } from "../../../../components/ui";
import { Card, CodeField, when } from "./parts";

type Props = { v: TwilioView; onChange: (n: TwilioView) => void };

export function ProviderCard({ v, onChange }: Props) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const target = v.preview_provider === "twilio" ? "mock" : "twilio";
  const go = async () => {
    setBusy(true);
    try { onChange(await twilioApi.provider(target, code)); setCode(""); toast.success(target === "twilio" ? "Preview now sends real texts to allow-listed numbers" : "Preview is back to simulated texts"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Preview text provider" icon={<Radio className="h-5 w-5 text-signal" />} testId="twilio-provider-card">
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Preview text provider">
        {(["mock", "twilio"] as const).map((m) => (
          <span key={m} role="radio" aria-checked={v.preview_provider === m} data-testid={`twilio-provider-${m}`}
            className={`rounded-full border px-3 py-1 text-sm ${v.preview_provider === m ? "border-signal bg-signal/15 text-white" : "border-white/10 text-zinc-400"}`}>
            {m === "mock" ? "Simulated (mock)" : "Real Twilio"}
          </span>
        ))}
        <span className="self-center text-xs text-zinc-500" data-testid="twilio-provider-mode">Now: {v.provider_mode}</span>
      </div>
      <p className="mt-3 text-sm text-zinc-400">Real Twilio sends real texts only to numbers on the allow-list below. Everyone else is recorded as BLOCKED_PREVIEW_ALLOWLIST and shows in the mock outbox.</p>
      {target === "twilio" && !v.can_switch_to_real && <p className="mt-2 text-sm text-amber-200" data-testid="twilio-provider-needs-test">Run Test connection and get a pass first.</p>}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-72"><CodeField value={code} onChange={setCode} id="twilio-provider-code" /></div>
        <Button onClick={go} loading={busy} disabled={code.length < 6 || (target === "twilio" && !v.can_switch_to_real)} data-testid="twilio-provider-switch">
          {target === "twilio" ? "Switch to Real Twilio" : "Switch to Simulated"}
        </Button>
      </div>
    </Card>
  );
}

export function AllowlistCard({ v, onChange }: Props) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const run = async (key: string, fn: () => Promise<TwilioView>, msg: string) => {
    setBusy(key);
    try { onChange(await fn()); setCode(""); setPhone(""); toast.success(msg); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };
  return (
    <Card title="Preview allow-list" icon={<ShieldCheck className="h-5 w-5 text-signal" />} testId="twilio-allowlist-card">
      <p className="text-sm text-zinc-400">The only numbers that can get real texts from Preview. Production ignores this list. Adding or removing needs a fresh authenticator code.</p>
      <ul className="mt-4 divide-y divide-white/5" data-testid="twilio-allowlist">
        {v.allowlist.map((a) => (
          <li key={a.phone_e164} className="flex items-center justify-between py-2 text-sm" data-testid={`twilio-allow-${a.phone_e164}`}>
            <span><span className="font-mono text-white">{a.phone_e164}</span><span className="ml-3 text-xs text-zinc-500">added {when(a.added_at)}{a.added_by === "SYSTEM" ? " · seeded" : ""}</span></span>
            <Button variant="ghost" className="h-8 px-2" aria-label={`Remove ${a.phone_e164}`} disabled={code.length < 6} loading={busy === a.phone_e164}
              onClick={() => run(a.phone_e164, () => twilioApi.disallow(a.phone_e164, code), "Number removed")} data-testid={`twilio-allow-remove-${a.phone_e164}`}><Trash2 className="h-4 w-4" /></Button>
          </li>
        ))}
        {v.allowlist.length === 0 && <li className="py-2 text-sm text-zinc-500">No numbers. Preview can't send any real texts.</li>}
      </ul>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-56"><TextInput value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+17025550123" aria-label="Phone number to allow" data-testid="twilio-allow-input" /></div>
        <div className="w-72"><CodeField value={code} onChange={setCode} id="twilio-allow-code" /></div>
        <Button variant="secondary" onClick={() => run("add", () => twilioApi.allow(phone, code), "Number added")} loading={busy === "add"} disabled={!phone || code.length < 6} data-testid="twilio-allow-add">Add number</Button>
      </div>
      <p className="mt-2 text-xs text-zinc-500">To remove a number, enter a code first, then press the bin icon.</p>
    </Card>
  );
}
